import { createClient } from "../supabase/client"
import { getCharacterPlatformBackendUrl } from "./server"
import { getDataProvider } from "../providers/data"
import type { Database } from "../supabase/types"
import type {
  DataProvider,
  BuildRow as DataProviderBuildRow,
  PublishedBuildsParams,
} from "../providers/data"

export type BuildRow = DataProviderBuildRow & {
  author_name?: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a JSON-string-or-already-parsed field safely. */
function parseJson<T>(val: string | T | null, fallback: T): T {
  if (val == null) return fallback
  if (typeof val !== "string") return val
  try {
    return JSON.parse(val)
  } catch {
    return fallback
  }
}

/** Convert a Supabase BuildRow into the shape BuildCard expects. */
export function buildRowToCardData(
  b: BuildRow,
  authorNames?: Map<string, string>
): {
  id: string
  name: string
  slug: string
  description: string
  author_name: string
  fork_count: number
  install_count: number
  stars: number
  tags: string[]
  skills: string[]
  grid_layout?: { slug: string; x: number; y: number; shape: string }[]
  is_featured: boolean
  is_first_party: boolean
  status: string
  chat_app_model: string | null
} {
  const skills: string[] = parseJson(b.skills, [])
  const tags: string[] = Array.isArray(b.tags)
    ? b.tags
    : parseJson(b.tags as string | null, [])
  const rawGrid = parseJson(b.grid_layout, [])
  const grid_layout =
    Array.isArray(rawGrid) && rawGrid.length > 0 ? rawGrid : undefined

  return {
    id: b.id,
    name: b.name,
    slug: b.slug,
    description: b.description ?? "",
    author_name: authorNames?.get(b.user_id) ?? b.author_name ?? "",
    fork_count: b.fork_count,
    install_count: b.install_count,
    stars: b.stars,
    tags,
    skills,
    grid_layout,
    is_featured: b.is_featured,
    is_first_party: false,
    status: b.status,
    chat_app_model: b.chat_app_model ?? null,
  }
}

// ---------------------------------------------------------------------------
// Response / request interfaces
// ---------------------------------------------------------------------------

export interface GenerateBuildResponse {
  build_id: string
  name: string
  description: string
  recommended_skills: string[]
  tags: string[]
  generation_status: string
}

export interface GenerationStatusResponse {
  build_id: string
  generation_status: string
  generation_error?: string
  description?: string
  identity_md?: string
  soul_md?: string
  user_md?: string
  agents_md?: string
  recommended_skills?: string[]
  tags?: string[]
}

export function createBuildReadHelpers(
  dataProvider: DataProvider = getDataProvider()
) {
  return {
    fetchViewerBuildById(buildId: string): Promise<BuildRow | null> {
      return dataProvider.viewerBuilds.getViewerBuildById(buildId)
    },
    fetchPublishedBuilds(
      params?: PublishedBuildsParams
    ): Promise<{ items: BuildRow[]; total: number }> {
      return dataProvider.publicBuilds.listPublishedBuilds(params)
    },
    fetchPublishedBuildBySlug(slug: string): Promise<BuildRow | null> {
      return dataProvider.publicBuilds.getPublishedBuildBySlug(slug)
    },
    fetchViewerBuilds(): Promise<BuildRow[]> {
      return dataProvider.viewerBuilds.listViewerBuilds()
    },
  }
}

const buildReadHelpers = createBuildReadHelpers()

export interface SaveBuildRequest {
  name: string
  description?: string
  status?: string
  identity_md?: string
  soul_md?: string
  user_md?: string
  agents_md?: string
  skills?: string[]
  grid_layout?: { slug: string; x: number; y: number; shape: string }[]
  original_prompt?: string
  tags?: string[]
  setup_command?: string | null
}

// ---------------------------------------------------------------------------
// Internal helper — backend fetch with auth
// ---------------------------------------------------------------------------

async function backendFetch(
  path: string,
  options: RequestInit = {}
): Promise<Response> {
  const supabase = createClient()
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new Error("Not authenticated")
  }

  const baseUrl = getCharacterPlatformBackendUrl()
  const url = new URL(path, baseUrl)

  const headers = new Headers(options.headers)
  headers.set("Authorization", `Bearer ${session.access_token}`)
  if (!headers.has("Content-Type") && options.body) {
    headers.set("Content-Type", "application/json")
  }

  return fetch(url, { ...options, headers })
}

// ---------------------------------------------------------------------------
// Public reads — data provider
// ---------------------------------------------------------------------------

export async function fetchViewerBuildById(
  buildId: string
): Promise<BuildRow | null> {
  return buildReadHelpers.fetchViewerBuildById(buildId)
}

export async function fetchPublishedBuilds(params?: {
  page?: number
  pageSize?: number
  search?: string
  tag?: string
  sort?: string
}): Promise<{ items: BuildRow[]; total: number }> {
  return buildReadHelpers.fetchPublishedBuilds(params)
}

export async function fetchPublishedBuildBySlug(
  slug: string
): Promise<BuildRow | null> {
  return buildReadHelpers.fetchPublishedBuildBySlug(slug)
}

export async function fetchViewerBuilds(): Promise<BuildRow[]> {
  return buildReadHelpers.fetchViewerBuilds()
}

// ---------------------------------------------------------------------------
// Backend mutations — auth required
// ---------------------------------------------------------------------------

export async function generateBuild(
  prompt: string,
  name?: string
): Promise<GenerateBuildResponse> {
  const res = await backendFetch("/v1/builds/generate", {
    method: "POST",
    body: JSON.stringify({ prompt, name }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Generate failed" }))
    throw new Error(body.detail || `Generate failed: ${res.status}`)
  }

  return res.json()
}

export async function pollGenerationStatus(
  buildId: string
): Promise<GenerationStatusResponse> {
  const res = await backendFetch(`/v1/builds/${buildId}/generation`)

  if (!res.ok) {
    const body = await res
      .json()
      .catch(() => ({ detail: "Failed to poll generation status" }))
    throw new Error(body.detail || `Poll failed: ${res.status}`)
  }

  return res.json()
}

export async function retryGeneration(
  buildId: string
): Promise<GenerateBuildResponse> {
  const res = await backendFetch(`/v1/builds/${buildId}/generation/retry`, {
    method: "POST",
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Retry failed" }))
    throw new Error(body.detail || `Retry failed: ${res.status}`)
  }

  return res.json()
}

export async function createBuild(data: SaveBuildRequest): Promise<BuildRow> {
  const res = await backendFetch("/v1/builds", {
    method: "POST",
    body: JSON.stringify(data),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Create failed" }))
    throw new Error(body.detail || `Create failed: ${res.status}`)
  }

  return res.json()
}

export async function updateBuild(
  slug: string,
  data: Partial<SaveBuildRequest>
): Promise<BuildRow> {
  const supabase = createClient()

  const updates: Database["public"]["Tables"]["builds"]["Update"] = {}
  if (data.name !== undefined) updates.name = data.name
  if (data.description !== undefined) updates.description = data.description
  if (data.status !== undefined) updates.status = data.status
  if (data.identity_md !== undefined) updates.identity_md = data.identity_md
  if (data.soul_md !== undefined) updates.soul_md = data.soul_md
  if (data.user_md !== undefined) updates.user_md = data.user_md
  if (data.agents_md !== undefined) updates.agents_md = data.agents_md
  if (data.skills !== undefined) updates.skills = JSON.stringify(data.skills)
  if (data.grid_layout !== undefined)
    updates.grid_layout = JSON.stringify(data.grid_layout)
  if (data.tags !== undefined) updates.tags = data.tags
  if (data.setup_command !== undefined)
    updates.setup_command = data.setup_command

  const { data: row, error } = await supabase
    .from("builds")
    .update(updates)
    .eq("slug", slug)
    .select()
    .single()

  if (error) throw new Error(`Update failed: ${error.message}`)
  return row
}

export async function forkBuild(slug: string): Promise<BuildRow> {
  const res = await backendFetch(`/v1/builds/${slug}/fork`, {
    method: "POST",
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Fork failed" }))
    throw new Error(body.detail || `Fork failed: ${res.status}`)
  }

  return res.json()
}

export interface DeleteBuildOptions {
  deleteOwnedSkills?: boolean
}

export async function deleteBuild(
  slug: string,
  options: DeleteBuildOptions = {}
): Promise<void> {
  const search = new URLSearchParams()
  if (options.deleteOwnedSkills) {
    search.set("delete_owned_skills", "true")
  }
  const suffix = search.toString() ? `?${search.toString()}` : ""
  const res = await backendFetch(`/v1/builds/${slug}${suffix}`, {
    method: "DELETE",
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Delete failed" }))
    throw new Error(body.detail || `Delete failed: ${res.status}`)
  }
}

export interface InstallBuildResponse {
  status: string
  message: string
  agent_id: string
  skills_installed: number
  skills_requested?: number
  brain_files_written: number
  setup_command?: string | null
  warnings?: string[]
}

export interface DeleteAgentResponse {
  status: string
  message: string
  agent_id: string
}

export async function deleteAgent(
  agentRowId: string
): Promise<DeleteAgentResponse> {
  const res = await backendFetch(`/v1/builds/agents/${agentRowId}`, {
    method: "DELETE",
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Delete failed" }))
    throw new Error(body.detail || `Delete failed: ${res.status}`)
  }

  return res.json()
}

export interface ShareAgentResponse {
  id: string
  slug: string
  name: string
  status: string
}

export async function shareAgent(
  agentRowId: string
): Promise<ShareAgentResponse> {
  const res = await backendFetch(`/v1/builds/agents/${agentRowId}/share`, {
    method: "POST",
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Share failed" }))
    throw new Error(body.detail || `Share failed: ${res.status}`)
  }

  return res.json()
}

export async function installBuild(
  slug: string,
  agentName?: string
): Promise<InstallBuildResponse> {
  const res = await backendFetch(`/v1/builds/${slug}/install`, {
    method: "POST",
    body: JSON.stringify({ agent_name: agentName ?? null }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: "Install failed" }))
    throw new Error(body.detail || `Install failed: ${res.status}`)
  }
  return res.json()
}

